import { SupplierModel } from '@/models/supplier';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';
import { ReportModel } from '@/models/report';
import { buildProductNameFilter } from './reportRowToExpansionFilter';

// fetchProductsWithOptions auto-appends `supplier` + `supplier_id` (see
// supplier.ts:898-900), so DO NOT include them here or they will appear twice
// in `reportColumns`. Also: that helper mutates the array we pass in, so
// always clone EXPANSION_COLUMNS at the call site to keep this constant pure.
export const EXPANSION_COLUMNS = [
  { code: 'variant_id',                dataType: 'number', name: 'Variant ID' },
  { code: 'variant_image',             dataType: 'image',  name: 'Image' },
  { code: 'Item[Item Description]',    dataType: 'string', name: 'Product' },
  { code: 'variant',                   dataType: 'string', name: 'Variant' },
  { code: 'product_sku',               dataType: 'string', name: 'SKU' },
  { code: '[inventory_replenishment]', dataType: 'number', name: 'Reorder Quantity' },
  { code: '[unit_cost]',               dataType: 'number', name: 'Unit Cost' },
];

interface SourceReport {
  filterColumns?: FilterColumnList | null;
  filterTimerange?: string[] | null;
  reportParams?: any;
  productSegment?: any;
  // Pass 2 (AS-9964): forward additional source-report inputs so the
  // by_product expansion reproduces the same numbers the user sees on the
  // source ReportPage. All optional — legacy callers keep working unchanged.
  analyticsType?: any;
  comparisonDate?: any;
  pivotOption?: string;
  sortBy?: any;
  sortDesc?: any;
}

export interface FetchExpansionArgs {
  productNames: string[];
  sourceReport: SourceReport;
}

export interface FetchExpansionResult {
  rows: any[];
  report: ReportModel | null;
}

export async function fetchExpansionRows(args: FetchExpansionArgs): Promise<FetchExpansionResult> {
  if (!args.productNames || args.productNames.length === 0) {
    return { rows: [], report: null };
  }

  const filters = new FilterColumnList();
  for (const f of (args.sourceReport.filterColumns?.items ?? [])) {
    filters.add(f);
  }
  const productFilterPayload = buildProductNameFilter(args.productNames);
  if (productFilterPayload) {
    const filter = new FilterColumnModel();
    filter.mapData(productFilterPayload);
    filters.add(filter);
  }

  // ReportPage stores dates as [start, end, label] (3 elements) — accept length >= 2.
  const tr = args.sourceReport.filterTimerange;
  const dateRange = (tr && tr.length >= 2 && tr[0] && tr[1])
    ? { start: tr[0], end: tr[1] }
    : undefined;

  // id=0 + operator='all' bypasses the supplier filter in fetchProductsWithOptions.
  const supplier = new SupplierModel({ id: 0 });
  const itemsPerPage = 500;
  // Pass 2: forward source-report inputs verbatim. Truthiness guards mean
  // legacy callers (sourceReport without these fields) produce the same
  // options payload they did before.
  const pageOptions: any = { page: 1, itemsPerPage };
  if (args.sourceReport.sortBy) { pageOptions.sortBy = args.sourceReport.sortBy; }
  if (args.sourceReport.sortDesc) { pageOptions.sortDesc = args.sourceReport.sortDesc; }
  const fetchOptions: any = {
    initialFilterColumns: filters,
    initialReportParams: args.sourceReport.reportParams,
    productSegment: args.sourceReport.productSegment,
    dateRange,
    pageOptions,
    // Clone — fetchProductsWithOptions mutates the columns array (.push for
    // supplier/supplier_id). Without the spread, every call would accumulate
    // duplicate columns on the module-level constant.
    columns: [...EXPANSION_COLUMNS],
    // true: skip the helper's `[inventory_replenishment] > 0` filter so we
    // also get suggestions for variants that don't currently need a reorder
    // (forecast/by-variant reports often select stable items). The user
    // wants "what would you reorder if you were to reorder", not "only
    // those flagged urgent".
    suggestFutureReorderQuantity: true,
  };
  if (args.sourceReport.analyticsType) { fetchOptions.analyticsType = args.sourceReport.analyticsType; }
  if (args.sourceReport.comparisonDate) { fetchOptions.comparisonDate = args.sourceReport.comparisonDate; }
  if (args.sourceReport.pivotOption) { fetchOptions.pivotOption = args.sourceReport.pivotOption; }
  const report = await supplier.fetchProductsWithOptions(
    'inventory_replenishment',
    'all',
    fetchOptions,
  );

  const rows = typeof (report as any)?.getReportRowsData === 'function'
    ? (report as any).getReportRowsData()
    : [];

  // The 50 selected-row cap upstream does not bound the *expanded* variant
  // count: a handful of high-variant products can still blow past this
  // page. Fail loud instead of silently truncating — the dialog would
  // otherwise drop variants the user expected to see.
  if (rows.length >= itemsPerPage) {
    throw new Error(
      `Expansion returned the first ${itemsPerPage} variants and may have been truncated. `
        + 'Narrow the selection or load variants incrementally.',
    );
  }

  return { rows, report };
}
